/**
 * ProblemPreviewV2 — v2 API 만 쓰는 문제 미리보기.
 *
 * 구형 ProblemPreview 는 **절대 손대지 않는다**(오너 지시 2026-08-26) — 그 파일은
 * 학습현황·시험결과·문제관리 등 여러 화면이 그대로 쓰고 있고, v1 경로 셋에 묶여 있다:
 *   /api/problems/print(본문) · /api/mathcepts/temp(개념 사전) · …/by-prob-ids(문제별 개념)
 *
 * 이 모달은 그 셋을 쓰지 않는다. 받는 길이 문항 수에 따라 둘이다:
 *   한 건  GET /api/v2/problems/{id}                                  → 상세(확정 전 문항도 열린다)
 *   여러 건 GET …/problem-search?problem_ids=…&with_body=1 (계약 §9)   → **왕복 한 번**
 * 둘 다 body_lexical · concept_names[{concept_id,name}] · difficulty 를 준다.
 * 여러 건을 상세로 치면 [고른 50개 보기]가 왕복 50회다 — 그래서 묶음을 쓰고, 검색이 거르는
 * 확정 전 문항(status≠999)만 상세로 보충한다.
 * 개념 사전은 여기에 더해 창당 한 번(모듈 캐시 5분)이고 문제 수와 무관하다 —
 * 구형이 문제마다 by-prob-ids 를 치던 것과 다른 점이다.
 *
 * 개념 배지의 **순번·유형 색칸**은 개념 사전에서 온다 — 그 둘은 개념 자체의 속성이라 문제 응답에
 * 없다. 2026-08-26 배포로 v2 사전(GET /api/v2/mathcepts, 계약 §8)이 생겨 **이제 채워진다.**
 * 구형이 v1(/api/mathcepts/temp)로 하던 일을 여기서는 conceptBadge.jsx 의 fetchConceptDictV2 가 한다
 * (권한 user_required — 교사 토큰으로도 열린다). 사전을 못 받으면 이름만 있는 배지로 남는다.
 *
 * 본문은 body_lexical(editorState)을 **HTML 로 굳혀** 그린다(lexicalHtml). 편집기를 마운트하지
 * 않는 이유는 여기가 읽기 전용이고, 무거운 의존(lexical·katex)을 창을 열 때에야 받기 때문이다.
 * 조판은 이 파일이 대지 않고 **ProblemBodyPreview 가 댄다** — 폭 423px·글자 18px·선지 격자가
 * 지면에서 온 한 벌이라, 카드형(ProblemCardsV2)과 같은 자리에서 줄이 꺾인다.
 *
 * 짝이 되는 카드형이 있다: **ProblemCardsV2**(페이지 안에 쌓이는 목록). 이 파일은 창이다.
 * 둘은 본문 렌더러·개념 배지·번호 칸·개념 파싱(conceptPairs)을 나눠 쓰고, 받는 시점만 다르다 —
 * 창은 열릴 때 한꺼번에, 목록은 눈에 든 것만 20건씩 묶어서.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { Modal, Button, MathceptDetailModal } from '../common';
import { useAuth } from '../../context/AuthContext';
import { fetchProblem } from '../ProblemHubV2/problemApi';
// 머리의 문제 번호 — 두 메뉴가 함께 쓰는 정본 부품(고정폭·굵게·상태색·문제 상세로 가는 링크)
import ProblemIdLink, { problemDetailPath } from '../ProblemHubV2/ProblemIdLink';
import { fetchProblemItemsV2 } from './problemBodyV2';
import { ConceptBadge, matchConcept, fetchConceptDictV2, fetchConceptDetailV2, conceptPairs } from './conceptBadge';
import { loadLexicalToHtml, parseDoc, toBodyHtmlMap } from './lexicalHtml';
// 본문 조판은 **이 부품이 대지 않는다** — 지면 규칙 한 벌(problemBodyPaper.css)을 가진
// ProblemBodyPreview 가 댄다. 종전에 여기 있던 주석은 「조판이 전역 스타일에 걸려 구형과
// 같은 얼굴이 나온다」고 했는데 **거짓이었다**: 전역에 스코프 없는 `.katex` 규칙이 0건이고
// `.PlaygroundEditorTheme__boxContent` 정의는 `.problem-preview-content`(index.css:463)와
// `.problem-body-paper` 로 둘 다 스코프가 걸려 이 창에 닿지 않았다. 구형이 같은 얼굴이던 것은
// 제 파일 안에 **스코프 없는 `<style>` 을 심기 때문**이고(ProblemPreview.js:499), 그것은
// 열려 있는 동안 화면 전체에 규칙을 흘린다. 그 방식을 따라가지 않고 스코프된 한 벌을 쓴다.
import ProblemBodyPreview from './ProblemBodyPreview';
import ProblemNumber from './ProblemNumber';
// 난이도 색은 **구형 미리보기와 같은 값**을 써야 한다(오너: 디자인 그대로).
// 주의 — 같은 이름의 상수가 styles/tokens.js 에도 있는데 그쪽은 **녹색 5단 그라데이션**으로
// 값이 전혀 다르다. 난이도 배지의 정본은 이 파일이고(색 하드코딩 룰을 여기서 disable 해 뒀다),
// ProblemPreviewCards 도 같은 것을 쓴다.
import { DIFFICULTY_COLORS } from '../../pages/LearningStatus/utils/constants';

/** 난이도 색 — 배열이라 1~5 가 인덱스 0~4 다. 척도 밖 값은 색을 주지 않는다(리터럴을 새로 만들지 않는다) */
const difficultyColor = (d) => DIFFICULTY_COLORS[d - 1] || null;

/**
 * 한 문제의 로딩 상태 — 실패를 문제 단위로 가둔다.
 * 구형은 하나가 깨지면 Promise.all 이 통째로 실패해 "인쇄할 문제가 없습니다"가 됐다.
 * 여기서는 깨진 것만 자리표시자로 남고 나머지는 정상으로 보인다.
 */
export function ProblemBlock({ id, idLabel = null, number, showNumbers, isCorrect, state, dict, html, onRetry = null, onConceptClick, onOpen = null }) {
  const { status, data, error } = state || { status: 'loading' };

  if (status === 'loading') {
    return (
      <div className="space-y-2">
        <div className="h-4 w-24 rounded bg-gray-100 motion-safe:animate-pulse" />
        <div className="h-24 w-full rounded-xl bg-gray-100 motion-safe:animate-pulse" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="space-y-2 rounded-xl border border-red-100 bg-red-50 p-3">
        <p role="alert" className="text-sm text-danger">
          {/* 문장 속 번호 — 색은 이 경고문(빨강)이 갖고 얼굴만 정본을 따른다 */}
          <ProblemIdLink id={id} chip linked={false} /> 문제를 불러오지 못했습니다{error ? ` — ${error}` : '.'}
        </p>
        {onRetry && <Button size="xs" variant="secondary" onClick={() => onRetry(id)}>다시 시도</Button>}
      </div>
    );
  }

  const concepts = conceptPairs(data);

  return (
    <div>
      <div className="mb-1 flex w-full items-center gap-2">
        {/* 은행 문제는 번호가 곧 이름이다 — 누르면 그 문제의 상세로 간다(오너 지시 2026-09-01).
            아직 등록되지 않은 산출(변형·생성 검수)에는 번호가 없어 부르는 쪽이 대신할 말을 준다.
            그때는 링크도 얼굴도 없다 — 없는 번호를 있는 것처럼 그리지 않는다.
            need_* 를 받지 못하는 자리라 색은 먹색으로 굳는다. */}
        {id != null
          ? <ProblemIdLink id={id} onOpen={onOpen} />
          : <span className="text-xs text-gray-500">{idLabel}</span>}
        {data.difficulty != null && (
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-bold text-white ${
              difficultyColor(data.difficulty) ? '' : 'bg-gray-400'
            }`}
            style={difficultyColor(data.difficulty) ? { backgroundColor: difficultyColor(data.difficulty) } : undefined}
          >
            Lv.{data.difficulty}
          </span>
        )}
      </div>

      {concepts.length > 0 && (
        <div className="mb-3 rounded-lg bg-gray-50 p-2">
          <div className="mb-1 flex items-center gap-2">
            <BookOpen aria-hidden="true" className="size-4 text-gray-600" />
            <span className="text-xs font-medium text-gray-600">수학 개념</span>
          </div>
          {/* 순번·유형 칸은 v2 사전(계약 §8)에서 온다. matchConcept 은 사전 항목의 `id` 로 찾는데,
              그 키는 fetchConceptDictV2 가 concept_id 를 얹어 만든 것이다 — 응답 원본에는 concept_id
              밖에 없다. 사전이 아직·영영 없으면 dict 가 null 이라 ConceptBadge 가 이름만 그린다. */}
          <div className="flex flex-wrap gap-1">
            {concepts.map((c) => {
              const m = matchConcept(dict, c.id);
              return (
                <ConceptBadge
                  key={`${c.id ?? 'x'}-${c.name}`}
                  name={c.name || m?.concept_name || m?.text || ''}
                  order={m?.order ?? null}
                  type={m?.concept_type ?? null}
                  // 눌러서 개념 설명을 여는 것은 **구형에 있던 기능**이다(ProblemPreview.js:418).
                  // 없애면 교체 뒤 「있던 게 없어졌다」가 된다. id 를 모르는 배지는 열 곳이 없어
                  // 눌리지 않게 둔다 — 사전이 안 붙었을 때가 그 경우다.
                  onClick={c.id != null && onConceptClick ? () => onConceptClick(c.id) : null}
                />
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-start gap-5">
        {/* 번호는 **시험지에 담긴 뒤에야 있는 값**이다. 담기 전 검색 결과에서 열 때는
            showNumbers 를 꺼서 이 칸을 아예 그리지 않는다 — 「01」이 붙으면 1번 문항으로 읽힌다.
            (구형 ProblemPreview 도 같은 이름의 스위치를 갖고 있고 담기 화면에서 끈다.)
            색은 **그 학생이 맞았나**를 말한다 — 문제은행을 보는 자리에서는 값이 없어 검정이다. */}
        {showNumbers && <ProblemNumber number={number} isCorrect={isCorrect} />}
        {/* 본문 — 폭·글자·조판을 **지면 값 한 벌**로 받는다(ProblemBodyPreview).
            직접 그리면 폭(422)만 지면이고 조판은 아니어서, 선지 ①~⑤ 가 세로로 다섯 줄 서고
            수식만 21% 크게 뜬다. 칸이 지면 폭(423)보다 좁으면 통째로 축소되므로 잘리지 않는다. */}
        <div className="min-w-0 flex-1">
          {html === null ? (
            <p className="text-sm text-gray-400">본문을 그리는 중…</p>
          ) : (
            <ProblemBodyPreview html={html} fallback={html === '' ? '본문이 비어 있습니다.' : null} />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * showNumbers 의 기본값이 **구형과 반대로 true** 다 — 신형의 첫 호출부(PaperItemsEditor)가
 * 담긴 문항을 순번과 함께 보여 주는 자리라, numbers 를 받는 설계 자체가 「번호를 그린다」를
 * 전제로 한다. 끄는 쪽(담기 전 검색 결과)만 명시적으로 false 를 준다.
 */
function ProblemPreviewV2({ isOpen, problemIds = [], numbers = [], correctList = [], showNumbers = true, onClose }) {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  /* 머리의 번호를 누르면 이 창을 닫고 그 문제의 상세로 간다.
     이 창은 다섯 화면이 띄운다(문제 v2 목록·문항 편집·담기 창·학습 허브·추이 드릴다운) —
     어느 쪽에서 열렸든 「이 문제를 제대로 보러 간다」는 한 가지 뜻이라 여기서 한 번만 정한다.
     미저장 편집 확인은 이 부품이 하지 않는다 — 화면이 <ProblemNavGuard> 로 담아 둔 것을
     ProblemIdLink 가 먼저 부르고, 취소하면 여기까지 오지 않는다. */
  const openProblem = useCallback((pid) => {
    if (pid == null) return;
    onClose && onClose();
    navigate(problemDetailPath(pid));
  }, [navigate, onClose]);
  const [states, setStates] = useState({}); // id -> {status,data,error}
  // 개념 설명 창 — { loading, data }. 배지를 눌러야 생긴다.
  // **v2 로 받아 data 를 주입한다**(ProblemViewTab:340 과 같은 길) — 안 주면 그 모달이
  // 제 v1 경로(/api/mathcepts/temp/by-concept-ids)로 직접 물어, 이 창이 v2 전용이 아니게 된다.
  const [conceptDetail, setConceptDetail] = useState(null);
  // 개념 사전(계약 §8) — 성공하면 배열, 실패하면 null. null 이면 배지가 이름만 남는다.
  // 모듈 캐시(5분)가 conceptBadge.jsx 안에 있어 같은 사전을 쓰는 다른 화면과 왕복을 나눠 쓴다.
  const [dict, setDict] = useState(null);
  const [htmls, setHtmls] = useState({}); // id -> HTML 문자열('' 이면 본문 없음, undefined 면 아직)
  const seqRef = useRef(0);
  const aliveRef = useRef(true);
  // StrictMode 는 마운트 → cleanup → 마운트를 돌린다 — 마운트마다 세우지 않으면 dev 에서
  // false 로 굳고, 그러면 응답이 와도 상태를 갱신하지 못해 **영원히 스켈레톤**이다.
  // (AssignPanel:74 이 같은 함정을 같은 주석으로 막아 두었다 — 그 패턴을 따른다.)
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const loadOne = useCallback(async (id, seq) => {
    setStates((prev) => ({ ...prev, [id]: { status: 'loading' } }));
    setHtmls((prev) => { const n = { ...prev }; delete n[id]; return n; });
    try {
      const d = await fetchProblem(id, getAccessToken);
      if (!aliveRef.current || seq !== seqRef.current) return;
      if (d == null) throw new Error('내용을 받지 못했습니다.');
      setStates((prev) => ({ ...prev, [id]: { status: 'ok', data: d } }));

      // 본문은 받은 뒤 한 번만 HTML 로 굳힌다. 실패해도 문제 자체는 이미 떠 있으므로
      // 본문 자리만 비운다 — 미리보기가 통째로 사라지지 않는다.
      const doc = parseDoc(d.body_lexical);
      if (doc == null) { setHtmls((prev) => ({ ...prev, [id]: '' })); return; }
      try {
        const conv = await loadLexicalToHtml();
        if (!aliveRef.current || seq !== seqRef.current) return;
        setHtmls((prev) => ({ ...prev, [id]: conv(doc) }));
      } catch (_) {
        if (aliveRef.current) setHtmls((prev) => ({ ...prev, [id]: '' }));
      }
    } catch (e) {
      if (!aliveRef.current || seq !== seqRef.current) return;
      setStates((prev) => ({ ...prev, [id]: { status: 'error', error: e.message } }));
    }
  }, [getAccessToken]);

  /**
   * 여러 건 — **묶음으로 한 번**에 받는다(계약 §9).
   *
   * 상세를 문제마다 치면 [고른 50개 보기] 한 번이 왕복 50회다. 검색은 problem_ids 로 묶어
   * 물을 수 있고 `with_body=1` 이면 본문까지 함께 와, 같은 화면이 **왕복 한 번**이 된다.
   *
   * ⚠ 검색은 **status=999(본문 확정) 문항만** 준다. 그래서 응답에 없는 id 는 실패가 아니라
   *   「아직 확정 전」일 수 있다 — 그것만 골라 상세로 한 번 더 묻는다. 이 보충이 없으면
   *   문제(/problem-hub)에서 확정 전 문제를 열었을 때 조용히 빈 창이 된다.
   */
  const loadMany = useCallback(async (ids, seq) => {
    setStates(ids.reduce((acc, id) => ({ ...acc, [id]: { status: 'loading' } }), {}));
    const map = await fetchProblemItemsV2(ids, getAccessToken, { withBody: true });
    if (!aliveRef.current || seq !== seqRef.current) return;
    // 묶음이 통째로 실패했다 — 상세로 하나씩 간다. 느리지만 화면은 뜬다.
    if (map == null) { ids.forEach((id) => loadOne(id, seq)); return; }

    const got = [];
    const missing = [];
    ids.forEach((id) => { (map[String(id)] ? got : missing).push(id); });

    setStates((prev) => {
      const next = { ...prev };
      got.forEach((id) => { next[id] = { status: 'ok', data: map[String(id)] }; });
      return next;
    });

    // 본문은 받은 뒤 한 번에 굳힌다. 변환기를 못 받아도 문제 자체는 이미 떠 있으므로
    // 본문 자리만 비운다 — 창이 통째로 사라지지 않는다.
    const docs = {};
    got.forEach((id) => {
      const v = map[String(id)]?.body_lexical;
      if (v != null) docs[String(id)] = v;
    });
    try {
      const html = Object.keys(docs).length > 0 ? await toBodyHtmlMap(docs) : {};
      if (!aliveRef.current || seq !== seqRef.current) return;
      // 본문이 없는 문항은 지도에 키가 안 들어오는 것이 계약이라, 빈 문자열로 자리를 채워
      // 「그리는 중…」에 머물지 않게 한다.
      setHtmls((prev) => {
        const next = { ...prev };
        got.forEach((id) => { next[String(id)] = html[String(id)] ?? ''; });
        return next;
      });
    } catch (_) {
      if (aliveRef.current && seq === seqRef.current) {
        setHtmls((prev) => {
          const next = { ...prev };
          got.forEach((id) => { next[String(id)] = ''; });
          return next;
        });
      }
    }

    // 확정 전이라 검색에 안 잡힌 것만 상세로 보충한다
    missing.forEach((id) => loadOne(id, seq));
  }, [getAccessToken, loadOne]);

  // 열릴 때 한 번. 문제 하나가 실패해도 나머지는 그대로 뜬다(allSettled 와 같은 효과 —
  // 각 요청이 자기 자리만 갱신하므로 서로를 무너뜨리지 않는다).
  const idsKey = problemIds.join(',');
  useEffect(() => {
    if (!isOpen || idsKey === '') { setStates({}); return; }
    const seq = (seqRef.current += 1);
    setStates({});
    setHtmls({});
    const ids = idsKey.split(',');
    // 한 건이면 상세가 낫다 — 왕복 수가 같고, 검색이 거르는 확정 전 문항도 그대로 열린다.
    if (ids.length === 1) loadOne(ids[0], seq);
    else loadMany(ids, seq);
  }, [isOpen, idsKey, loadOne, loadMany]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    Promise.resolve(fetchConceptDictV2(getAccessToken))
      .then((d) => { if (alive) setDict(Array.isArray(d) ? d : null); })
      .catch(() => { if (alive) setDict(null); });
    return () => { alive = false; };
  }, [isOpen, getAccessToken]);

  const retry = useCallback((id) => { loadOne(id, seqRef.current); }, [loadOne]);

  const openConcept = useCallback(async (conceptId) => {
    if (conceptId == null) return;
    setConceptDetail({ loading: true, data: [] });
    const list = await fetchConceptDetailV2([conceptId], getAccessToken);
    // 못 받으면 **창을 닫는다.** 빈 창을 띄워 두면 「설명이 없는 개념」으로 읽힌다.
    setConceptDetail(list == null ? null : { loading: false, data: list });
  }, [getAccessToken]);

  // 창이 닫히면 개념 설명도 함께 접는다 — 다음에 열 때 남아 있으면 안 누른 창이 떠 있다
  useEffect(() => { if (!isOpen) setConceptDetail(null); }, [isOpen]);

  if (!isOpen) return null;

  // 폭·모서리·머리는 구형과 같은 값이다 — 구형은 자체 껍질이었고 여기는 공용 Modal 이라
  // role=dialog·포커스 가둠·ESC 가 함께 온다. 겉모습에서 다른 것은 닫기 단추 모양뿐이다.
  // size 목록에 480 이 없어 max-w 만 덮는다(뒤에 오는 클래스라 ! 로 못박는다).
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      /* 제목 **바로 오른쪽**에 세대를 붙인다(오너 지시). 같은 이름의 창이 두 벌 살아 있는 동안
         (구형은 대상 밖 화면에 그대로 남는다) 「지금 보는 게 어느 쪽인가」를 물어보지 않고
         구별할 표시가 필요하다.
         · title 에 노드를 넘기는 것은 공용 Modal 이 받아 주는 형태다(TimepointProblemModal 선례).
         · 감싸는 h2 가 text-xl/font-bold/gray-800 이라 **셋을 모두 되돌려** 보조 층위로 내린다 —
           크기·굵기·색을 함께 움직인다(색만으로 내리면 색을 못 보는 사람에게 위계가 사라진다).
         · 크기·색 값은 새로 정하지 않고 헤더의 형제 보조 표시와 같은 것을 쓴다
           (ProblemPickerModal 의 「N건」 — text-xs text-gray-400).
         · items-baseline — 크기가 다른 두 글자가 나란히 설 때는 기준선을 맞춘다. */
      title={(
        <span className="inline-flex items-baseline gap-1.5">
          문제 미리보기
          <span className="text-xs font-normal text-gray-400">v2</span>
        </span>
      )}
      size="md"
      maxHeight="90vh"
      className="md:!max-w-[480px]"
    >
      <div className="space-y-8">
        {problemIds.map((id, i) => (
          <ProblemBlock
            key={id}
            id={id}
            /* 번호를 누르면 이 창을 닫고 그 문제로 간다 — 닫지 않으면 배경만 조용히 바뀌고
               창은 열린 채 남아, 방금 무슨 일이 일어났는지 보이지 않는다. */
            onOpen={openProblem}
            number={numbers[i] ?? i + 1}
            showNumbers={showNumbers}
            // 자리마다 값이 없을 수 있다 — 문제은행 화면은 correctList 자체를 안 넘긴다
            isCorrect={correctList[i] ?? null}
            state={states[String(id)] || states[id]}
            dict={dict}
            html={htmls[String(id)] ?? htmls[id] ?? null}
            onRetry={retry}
            onConceptClick={openConcept}
          />
        ))}
      </div>
      {/* 개념 설명 — 이 창 **안에** 둔다. 공용 Modal 이 겹쳐 뜨며 제 z 를 갖는다. */}
      {conceptDetail && (
        <MathceptDetailModal
          isOpen
          onClose={() => setConceptDetail(null)}
          data={conceptDetail.data}
          loading={conceptDetail.loading}
        />
      )}
    </Modal>
  );
}

export default ProblemPreviewV2;
